import { Download, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import { purpleColors, violetColors } from '../styles';
import { APP_STORE_URL } from '../constants';

type CTAProps = {};

const getLocalizedAppStoreBadge = (language: string): string => {
  const normalized = language.toLowerCase();
  if (normalized.startsWith('de')) {
    return '/images/Applandingpage/AppStore_downloadbutton/Download_on_the_App_Store_Badge_DE_RGB_blk_092917.svg';
  }
  if (normalized.startsWith('fr')) {
    return '/images/Applandingpage/AppStore_downloadbutton/Download_on_the_App_Store_Badge_FR_RGB_blk_100517.svg';
  }
  if (normalized.startsWith('nl')) {
    return '/images/Applandingpage/AppStore_downloadbutton/Download_on_the_App_Store_Badge_NL_RGB_blk_100317.svg';
  }
  return '/images/Applandingpage/AppStore_downloadbutton/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
};

const CTASection = styled.section`
  padding: 6rem 1.5rem;
  background: linear-gradient(to bottom right, ${purpleColors[600]}, ${purpleColors[700]}, ${violetColors[600]});
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 32px 32px;
  }
  
  @media (min-width: 768px) {
    padding: 6rem 3rem;
  }
`;

const CTAContainer = styled.div`
  position: relative;
  z-index: 10;
  max-width: 56rem;
  margin: 0 auto;
  text-align: center;
`;

const PrimaryCtaSection = styled.div`
  margin-bottom: 2.5rem;
`;

const Heading = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const Description = styled.p`
  font-size: 1.25rem;
  color: ${purpleColors[100]};
  margin-bottom: 1.25rem;
  line-height: 1.6;
`;

const AppStoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  margin-bottom: 0.875rem;
`;

const AppStoreBadgeImage = styled.img`
  height: 4.5rem;
  width: auto;
  display: block;
`;

const DownloadButton = styled.a`
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.14);
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 4px 10px -4px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.45);
    box-shadow: 0 8px 16px -8px rgba(0, 0, 0, 0.3);
    
    svg:last-child {
      transform: translateX(0.125rem);
    }
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    
    &:last-child {
      transition: transform 0.2s;
    }
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 48rem;
  margin: 0 auto;
  padding: 1.75rem 1.25rem;
  border-radius: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);

  @media (min-width: 768px) {
    padding: 2rem 2rem;
  }
`;

const EarlyAccessSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.875rem;
  text-align: center;
  margin-bottom: 0.75rem;
`;

const EarlyAccessTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const EarlyAccessSubtitle = styled.p`
  color: ${purpleColors[100]};
  font-size: 0.9375rem;
  line-height: 1.5;
  margin: 0;
  max-width: 38rem;
`;

const IOSLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.75rem;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  span {
    font-size: 0.875rem;
    font-weight: 500;
  }
`;

const NotOnIosHeading = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 32rem;
  margin-bottom: 0.25rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    gap: 1.5rem;
  }
`;

const Option = styled.a`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  text-decoration: none;
  transition: all 0.2s;
  flex: 1;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
`;

const OptionTitle = styled.div`
  color: white;
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const OptionSubtitle = styled.p`
  color: ${purpleColors[100]};
  font-size: 0.8125rem;
  line-height: 1.5;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const NotOnIosTestFlightDivider = styled.hr`
  border: none;
  align-self: stretch;
  height: 1px;
  margin: 1.5rem 0 0.25rem;
  background: rgba(255, 255, 255, 0.22);
`;

export default function CTA({}: CTAProps) {
  const { t, i18n } = useTranslation();
  const appStoreBadgeSrc = getLocalizedAppStoreBadge(i18n.language);

  return (
    <CTASection id="download">
      <CTAContainer>
        <PrimaryCtaSection>
          <Heading>{t('appLandingPage.cta.heading')}</Heading>

          <Description>{t('appLandingPage.cta.description')}</Description>
          <AppStoreButton
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              mixpanel.track('Download on App Store link clicked', {
                page: window.location.pathname,
              });
            }}
          >
            <AppStoreBadgeImage
              src={appStoreBadgeSrc}
              alt="Download on the App Store"
            />
          </AppStoreButton>
          <IOSLabel>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{t('appLandingPage.cta.iosLabel')}</span>
          </IOSLabel>
        </PrimaryCtaSection>

        <InfoSection>
          <NotOnIosHeading>{t('appLandingPage.cta.notOnIos')}</NotOnIosHeading>
          <OptionsContainer>
            <Option
              href="/"
              onClick={() => {
                mixpanel.track('Use our webapp link clicked', {
                  page: window.location.pathname,
                });
              }}
            >
              <OptionTitle>
                {t('appLandingPage.cta.optionWebappTitle')}
              </OptionTitle>
              <OptionSubtitle>
                {t('appLandingPage.cta.optionWebappSubtitle')}
              </OptionSubtitle>
            </Option>
            <Option
              href="#newsletter"
              onClick={() => {
                mixpanel.track('Sign up for Android link clicked', {
                  page: window.location.pathname,
                });
              }}
            >
              <OptionTitle>
                {t('appLandingPage.cta.optionAndroidTitle')}
              </OptionTitle>
              <OptionSubtitle>
                {t('appLandingPage.cta.optionAndroidSubtitle')}
              </OptionSubtitle>
            </Option>
          </OptionsContainer>
          <NotOnIosTestFlightDivider aria-hidden />
          <EarlyAccessSection>
            <EarlyAccessTitle>{t('appLandingPage.cta.earlyAccessTitle')}</EarlyAccessTitle>
            <EarlyAccessSubtitle>
              {t('appLandingPage.cta.earlyAccessSubtitle')}
            </EarlyAccessSubtitle>
            <DownloadButton
              href="https://testflight.apple.com/join/arhpbwmz"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                mixpanel.track('Download on TestFlight link clicked', {
                  page: window.location.pathname,
                });
              }}
            >
              <Download />
              {t('appLandingPage.cta.button')}
              <ArrowRight />
            </DownloadButton>
          </EarlyAccessSection>
        </InfoSection>
      </CTAContainer>
    </CTASection>
  );
}

